import * as Yup from "yup";
import { Autocomplete, Divider, TextField, Typography } from "@mui/material";
import { capitalize } from "common/utils/strings";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { Formik } from "formik";
import { Job, Objective } from "protos/rtc/jobs";
import { jobExplorer } from "common/state/jobExplorer";
import { NumberedTractorIcon } from "common/components/map/NumberedTractorIcon";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";
import React, { FC, useMemo } from "react";

interface AlignAllRowsModalProps {
  job: Job;
  open: boolean;
  onSave?: () => void;
  onClose?: () => void;
}

interface AlignAllRowsFormValues {
  objectiveId: number | undefined;
  robotSerial: string | undefined;
}

const initialValues: AlignAllRowsFormValues = {
  objectiveId: undefined,
  robotSerial: undefined,
};

export const AlignAllRowsModal: FC<AlignAllRowsModalProps> = ({
  job,
  open,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const colorsBySerial = useSelector(
    jobExplorer.selectors.selectColorsBySerial
  );

  // todo: we plan to limit this selection to only objectives which have acceptable progress
  // (we want a good amount of history data to get an accurate average)
  // Our progress percent is not reliable for this, but we plan to improve it over time.
  const sufficientProgressObjectives: Objective[] = useMemo(() => {
    return job.objectives;
  }, [job]);

  const { data: assignments } = rtcJobsApi.endpoints.listAssignments.useQuery({
    objectiveIds: sufficientProgressObjectives.map((o) => o.id.toString()),
  });

  const objectivesAndAssignments = useMemo(() => {
    return sufficientProgressObjectives
      .map((o) => ({
        ...o,
        assignment: assignments?.assignments.find(
          (oa) => oa.objectiveId === o.id
        ),
      }))
      .filter((o) => o.assignment);
  }, [sufficientProgressObjectives, assignments]);

  const [alignAllRows] = useMutationPopups(
    rtcJobsApi.endpoints.alignAllRows.useMutation(),
    {
      success: capitalize(t("utils.actions.updated")),
    }
  );

  const validationSchema = Yup.object({
    robotSerial: Yup.string().required(t("utils.form.required")),
    objectiveId: Yup.number().required(t("utils.form.required")),
  });

  const handleSubmit = async (
    values: AlignAllRowsFormValues
  ): Promise<void> => {
    if (values.objectiveId === undefined || values.robotSerial === undefined) {
      return;
    }
    await alignAllRows({
      jobId: job.id,
      objectiveId: values.objectiveId,
      robotSerial: values.robotSerial,
      updatePlantingHeading: true,
    });
    onSave?.();
  };

  if (!open) {
    return;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        validateForm,
        isSubmitting,
        handleReset,
        errors,
        touched,
        submitForm,
      }) => (
        <ConfirmationDialog
          title={t("views.autotractor.alignAllRows.title")}
          yesText={t("views.autotractor.alignAllRows.title")}
          yesDisabled={
            isSubmitting ||
            Boolean(errors.objectiveId) ||
            Boolean(errors.robotSerial)
          }
          onYes={submitForm}
          onClose={() => {
            handleReset();
            onClose?.();
          }}
          description={
            <>
              <div className="flex gap-1">
                <InfoIcon fontSize="small" />
                <Typography className="flex my-auto">
                  {t("views.autotractor.alignAllRows.warning")}
                </Typography>
              </div>
              <Divider />
              <div className="flex gap-4 mt-2">
                <Autocomplete
                  fullWidth
                  options={objectivesAndAssignments}
                  value={
                    objectivesAndAssignments.find(
                      (o) => o.id === values.objectiveId
                    ) ?? undefined
                  }
                  renderOption={(props, option) => (
                    <Typography {...props}>
                      <Typography className="flex my-auto mr-2">
                        {option.name}
                      </Typography>
                      {option.assignment?.robotSerial && (
                        <div className="flex my-auto">
                          <NumberedTractorIcon
                            serial={option.assignment.robotSerial}
                            color={
                              colorsBySerial[option.assignment.robotSerial]
                            }
                          />
                        </div>
                      )}
                    </Typography>
                  )}
                  onChange={(_, v) => {
                    // work around https://github.com/jaredpalmer/formik/issues/1191
                    // and https://github.com/jaredpalmer/formik/issues/2083
                    setFieldValue("objectiveId", v?.id).finally(() =>
                      setFieldTouched("objectiveId", true)
                    );
                    setFieldValue(
                      "robotSerial",
                      v?.assignment?.robotSerial
                    ).finally(() => setFieldTouched("robotSerial", true));
                    validateForm();
                  }}
                  getOptionLabel={(o) => o.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={capitalize(
                        t("models.autotractor.objective", { count: 1 })
                      )}
                      error={touched.objectiveId && Boolean(errors.objectiveId)}
                      helperText={
                        touched.objectiveId && errors.objectiveId
                          ? errors.objectiveId
                          : ""
                      }
                    />
                  )}
                />
              </div>
            </>
          }
        />
      )}
    </Formik>
  );
};
